import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';
import { SortDirectionFinancing } from '../models/request-response-all-java';
import { ConnectableObservable, Observable, Subscription, Subject } from 'rxjs';
import { filter, map, shareReplay, first, switchMap, startWith, scan } from 'rxjs/operators';
import { ILsqDataSource } from './lsq-data-source';
import { SortDirectionEnterprise } from '../models/request-response';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class LsqListSource<RowType> implements DataSource<RowType>, ILsqDataSource {
    private _dataSubscription: Subscription;
    private _rowsSubscription: Subscription;
    private _spliceRowsSubscription: Subscription;
    private _noDataSubscription: Subscription;
    private _loadingSubscription: Subscription;
    private _errorSubscription: Subscription;
    private _totalCountSubscription: Subscription;
    protected _spliceRows$ = new Subject<[number, number, RowType[]]>();

    public data$: Observable<any> | ConnectableObservable<any>;
    public rows$: Observable<RowType[]> | ConnectableObservable<RowType[]>;
    public noData$?: Observable<boolean> | ConnectableObservable<boolean>;
    public loading$?: Observable<boolean> | ConnectableObservable<boolean>;
    public error$?: Observable<any> | ConnectableObservable<any>;
    public totalCount$?: Observable<number> | ConnectableObservable<number>;
    public skip: number;
    public take: number;
    public sortDirection: SortDirectionEnterprise | SortDirectionFinancing;
    public sortColumn: string;
    public handleSkipChange?: (skip: number) => void;
    public handleTakeChange?: (take: number) => void;
    public handleSort?: (sorting: { sortColumn: string; sortDirection: any }) => void;

    constructor(config: Partial<LsqListSource<RowType>>) {
        this.data$ = config.data$
            ? config.data$.pipe(
                  filter((data) => !!data),
                  shareReplay(1)
              )
            : undefined;
        this.rows$ = config.rows$
            ? config.rows$.pipe(
                  filter((rows) => !!rows),
                  switchMap((rows) =>
                      this._spliceRows$.pipe(
                          startWith(undefined),
                          scan((previousRows, spliceArgs) => {
                              if (spliceArgs) {
                                  const [
                                      indexToSplice,
                                      numberOfRowsToRemove,
                                      rowsToAdd
                                  ] = spliceArgs;
                                  if (numberOfRowsToRemove > 0 || rowsToAdd.length) {
                                      const splicedRows = [...previousRows];
                                      splicedRows.splice(
                                          indexToSplice + 1,
                                          numberOfRowsToRemove,
                                          ...rowsToAdd
                                      );
                                      return splicedRows;
                                  }
                              }
                              return previousRows;
                          }, rows)
                      )
                  ),
                  shareReplay(1)
              )
            : undefined;
        this.noData$ = config.noData$
            ? config.noData$.pipe(shareReplay(1))
            : this.rows$.pipe(
                  map((rows) => !rows || !rows.length),
                  shareReplay(1)
              );
        this.loading$ = config.loading$ ? config.loading$.pipe(shareReplay(1)) : undefined;
        this.error$ = config.error$ ? config.error$.pipe(shareReplay(1)) : undefined;
        this.totalCount$ = config.totalCount$ ? config.totalCount$.pipe(shareReplay(1)) : undefined;
        this.skip = config.skip;
        this.take = config.take;
        this.sortColumn = config.sortColumn;
        this.sortDirection = config.sortDirection;
        this.handleSkipChange = config.handleSkipChange;
        this.handleTakeChange = config.handleTakeChange;
        this.handleSort = config.handleSort;

        this._makeExistingObservablesHot();
    }

    public connect(_collectionViewer?: CollectionViewer): Observable<RowType[]> {
        this._makeExistingObservablesHot();
        return this.rows$;
    }

    public disconnect(_collectionViewer?: CollectionViewer): void {
        // Dispose of our hot observables.
        if (this._dataSubscription) {
            this._dataSubscription.unsubscribe();
        }
        if (this._rowsSubscription) {
            this._rowsSubscription.unsubscribe();
        }
        if (this._spliceRowsSubscription) {
            this._spliceRowsSubscription.unsubscribe();
        }
        if (this._noDataSubscription) {
            this._noDataSubscription.unsubscribe();
        }
        if (this._loadingSubscription) {
            this._loadingSubscription.unsubscribe();
        }
        if (this._errorSubscription) {
            this._errorSubscription.unsubscribe();
        }
        if (this._totalCountSubscription) {
            this._totalCountSubscription.unsubscribe();
        }
    }

    public expandGroup(index: number, rows: RowType[]): void {
        this._spliceRows$.next([index, 0, rows]);
    }

    /**
     * `commonRefKeyName` is a key that maps to the same value (e.g. a primary key) on the parent row
     * AND all the child rows.
     * @example Use this method if the parent row looks like `{ refInvoiceKey: '1', isParent: true }`
     * and the child rows look like `{ refInvoiceKey: '1', isChild: true }` — in other words, if the
     * parent and child rows have this same key/value pair in common.
     */
    public async collapseGroup(commonRefKeyName: string, commonRefKeyValue: string): Promise<void> {
        const rows = await this.rows$.pipe(first()).toPromise();
        const allRowsWithCommonRefKey = rows.filter(
            (row) => row[commonRefKeyName] === commonRefKeyValue
        );
        const indexOfParentRow = rows.findIndex(
            (row) => row[commonRefKeyName] === commonRefKeyValue
        );
        this._spliceRows$.next([indexOfParentRow, allRowsWithCommonRefKey.slice(1).length, []]);
    }

    /**
     * These arguments are a key/value pair shared across all the child rows.
     * @example Use this method if the parent row does NOT have a key/value pair in common with its children.
     */
    public async collapseGroupAlt(
        childRowLookupKey: string,
        childRowLookupValue: string
    ): Promise<void> {
        const rows = await this.rows$.pipe(first()).toPromise();
        const allRowsWithCommonRefKey = rows.filter(
            (row) => row[childRowLookupKey] === childRowLookupValue
        );
        const indexOfParentRow =
            rows.findIndex((row) => row[childRowLookupKey] === childRowLookupValue) - 1;
        this._spliceRows$.next([indexOfParentRow, allRowsWithCommonRefKey.length, []]);
    }

    private _makeExistingObservablesHot(): void {
        // Kick off our hot observables.
        if (this.data$ && !this._dataSubscription) {
            this._dataSubscription = this.data$.subscribe();
        }
        if (this.rows$ && !this._rowsSubscription) {
            this._rowsSubscription = this.rows$.subscribe();
        }
        if (this._spliceRows$ && !this._spliceRowsSubscription) {
            this._spliceRowsSubscription = this._spliceRows$.subscribe();
        }
        if (this.noData$ && !this._noDataSubscription) {
            this._noDataSubscription = this.noData$.subscribe();
        }
        if (this.loading$ && !this._loadingSubscription) {
            this._loadingSubscription = this.loading$.subscribe();
        }
        if (this.error$ && !this._errorSubscription) {
            this._errorSubscription = this.error$.subscribe();
        }
        if (this.totalCount$ && !this._totalCountSubscription) {
            this._totalCountSubscription = this.totalCount$.subscribe();
        }
    }
}
